import { useState } from "react";
import { IconDownload, IconEye } from "@tabler/icons-react";
import dayjs from "dayjs";
import { Badge, Checkbox, Group, IconAction, Loader, Paper, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { toastError } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";
import {
  descargarDocumento,
  previsualizarDocumento,
  type DocumentoHistorico,
} from "../services/historico.api";

/** Tamaño legible · el spec pide mostrarlo (útil antes de exportar). */
export function formatearTamano(bytes: number): string {
  if (bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FilaHistorico({
  doc,
  seleccionado,
  onSeleccionar,
}: {
  doc: DocumentoHistorico;
  seleccionado: boolean;
  onSeleccionar: (checked: boolean) => void;
}) {
  const [descargando, setDescargando] = useState(false);
  const [viendo, setViendo] = useState(false);

  const accion = async (
    fn: (d: DocumentoHistorico) => Promise<void>,
    setBusy: (b: boolean) => void,
    titulo: string,
  ) => {
    setBusy(true);
    try {
      await fn(doc);
    } catch (err) {
      toastError({ title: titulo, message: extractApiMessage(err, "No se encontró el archivo seleccionado.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tr>
      <Td>
        <Checkbox
          checked={seleccionado}
          onCheckedChange={(checked) => onSeleccionar(checked === true)}
          aria-label={`Seleccionar ${doc.nombreArchivo}`}
        />
      </Td>
      <Td>
        <Text size="sm" fw={500}>
          {doc.folio}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{dayjs(doc.fechaSolicitud).format("DD/MM/YYYY")}</Text>
      </Td>
      <Td>
        {doc.proveedores.length === 0 ? (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ) : (
          <Stack gap={2}>
            {doc.proveedores.map((p) => (
              <div key={p.numeroAcreedor}>
                <Text size="sm" truncate title={p.razonSocial}>
                  {p.razonSocial}
                </Text>
                <Text size="xs" c="dimmed">
                  {p.rfc} · acreedor {p.numeroAcreedor}
                </Text>
              </div>
            ))}
          </Stack>
        )}
      </Td>
      <Td className="max-w-70">
        {/* Nombres largos truncados con el completo en hover (spec §7.1). */}
        <Text size="sm" truncate title={doc.nombreArchivo}>
          {doc.nombreArchivo}
        </Text>
      </Td>
      <Td>
        <Badge variant="light" size="sm">
          {doc.clasificacion}
        </Badge>
      </Td>
      <Td align="right">
        <Text size="sm" c="dimmed">
          {formatearTamano(doc.tamanoBytes)}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{doc.organizacionNombre ?? "—"}</Text>
      </Td>
      <Td>
        <Group gap={4} justify="center" className="flex-nowrap">
          <IconAction label="Vista previa" size="sm" loading={viendo} onClick={() => accion(previsualizarDocumento, setViendo, "Vista previa")}>
            <IconEye size={16} stroke={1.7} />
          </IconAction>
          <IconAction label="Descargar" size="sm" loading={descargando} onClick={() => accion(descargarDocumento, setDescargando, "Descarga")}>
            <IconDownload size={16} stroke={1.7} />
          </IconAction>
        </Group>
      </Td>
    </Tr>
  );
}

/**
 * Tabla del Histórico Documental · paginada en servidor, con selección
 * múltiple para la exportación ZIP y acciones de ver/descargar por fila
 * (siempre por DocumentoId · el cliente jamás conoce rutas · spec §9).
 */
export function HistoricoTable({
  documentos,
  seleccion,
  onSeleccion,
  buscando = false,
  mensajeInformativo,
}: {
  documentos: DocumentoHistorico[];
  seleccion: ReadonlySet<string>;
  onSeleccion: (ids: Set<string>) => void;
  buscando?: boolean;
  mensajeInformativo: string | null;
}) {
  const idsPagina = documentos.map((d) => d.documentoId);
  const todosSeleccionados = idsPagina.length > 0 && idsPagina.every((id) => seleccion.has(id));
  const algunoSeleccionado = idsPagina.some((id) => seleccion.has(id));

  const alternarPagina = (checked: boolean) => {
    const nueva = new Set(seleccion);
    for (const id of idsPagina) {
      if (checked) nueva.add(id);
      else nueva.delete(id);
    }
    onSeleccion(nueva);
  };

  const alternarUno = (id: string, checked: boolean) => {
    const nueva = new Set(seleccion);
    if (checked) nueva.add(id);
    else nueva.delete(id);
    onSeleccion(nueva);
  };

  return (
    <Paper className="relative overflow-hidden">
      {buscando && (
        <Group gap={6} className="absolute top-2 right-3 z-10 pointer-events-none">
          <Loader size="xs" />
          <Text size="xs" c="dimmed" fw={500}>
            Buscando…
          </Text>
        </Group>
      )}
      <div className="overflow-x-auto">
        <Table verticalSpacing="sm" minWidth={960}>
          <Thead>
            <Tr>
              <Th className="w-10">
                <Checkbox
                  checked={algunoSeleccionado && !todosSeleccionados ? "indeterminate" : todosSeleccionados}
                  onCheckedChange={(checked) => alternarPagina(checked === true)}
                  aria-label="Seleccionar todos los documentos de la página"
                />
              </Th>
              <Th>Folio</Th>
              <Th>Fecha solicitud</Th>
              <Th>Proveedor</Th>
              <Th>Archivo</Th>
              <Th>Clasificación</Th>
              <Th align="right">Tamaño</Th>
              <Th>Sociedad</Th>
              <Th align="center">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {documentos.length === 0 ? (
              <Tr>
                <Td colSpan={9}>
                  {/* RN-06 · mensaje específico del backend, no un "sin datos" genérico. */}
                  <Text size="sm" c="dimmed" ta="center" className="py-5">
                    {mensajeInformativo ?? "No se encontró documentación para los filtros."}
                  </Text>
                </Td>
              </Tr>
            ) : (
              documentos.map((d) => (
                <FilaHistorico
                  key={d.documentoId}
                  doc={d}
                  seleccionado={seleccion.has(d.documentoId)}
                  onSeleccionar={(checked) => alternarUno(d.documentoId, checked)}
                />
              ))
            )}
          </Tbody>
        </Table>
      </div>
    </Paper>
  );
}
